import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CaretDown, Info, Trash, UploadSimple, UserCircle } from 'phosphor-react'
import SelectComponent from 'components/common/select/SelectComponent'

const speakerSelectStyles = {
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected || state.isFocused ? '#F6F8FA' : '#FFFFFF',
    color: '#0a0d14',
    padding: '8px 12px',
    fontWeight: 400,
    fontSize: '14px',
    lineHeight: '20px',
    cursor: 'pointer',
  }),
  control: (provided, state) => ({
    ...provided,
    display: 'flex',
    width: '100%',
    minHeight: '40px',
    height: '40px',
    background: '#FFFFFF',
    borderRadius: '10px',
    border: '1px solid #e2e4e9',
    boxShadow: state.isFocused
      ? '0 0 0 1px #e2e4e9'
      : '0 1px 2px rgba(228, 229, 231, 0.24)',
    '&:hover': {
      borderColor: '#e2e4e9',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 12px',
    height: '38px',
  }),
  placeholder: (provided) => ({
    ...provided,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#868c98',
    margin: 0,
  }),
  singleValue: (provided) => ({
    ...provided,
    fontSize: '14px',
    lineHeight: '20px',
    color: '#0a0d14',
    margin: 0,
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    paddingRight: '8px',
    height: '38px',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    padding: '0 4px',
    color: '#868c98',
  }),
  menu: (provided) => ({
    ...provided,
    marginTop: 4,
    padding: '4px 0',
    background: '#FFFFFF',
    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.12)',
    borderRadius: '10px',
    border: '1px solid #e2e4e9',
    overflow: 'hidden',
    zIndex: 20,
  }),
  menuList: (provided) => ({
    ...provided,
    padding: 0,
    maxHeight: 220,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
}

function SpeakerAccordionCard({
  title,
  isOpen,
  onToggle,
  speaker,
  speakerOptions,
  speakersLoading,
  errors,
  onSelectSpeaker,
  onDelete,
  submitCount,
}) {
  const [fieldTouched, setFieldTouched] = useState({})
  const initialSubmitCountRef = useRef(submitCount)
  const shouldShowSubmitErrors = submitCount > initialSubmitCountRef.current

  const markTouched = (field) => {
    setFieldTouched((prev) => ({ ...prev, [field]: true }))
  }

  const selectedSpeakerValue =
    speaker?.userId ||
    speakerOptions.find((option) => option.label === speaker?.name)?.value ||
    ''

  return (
    <div className='event-speaker-card'>
      <div className='event-speaker-card__header'>
        <button
          type='button'
          className='event-speaker-card__toggle'
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          <CaretDown
            size={18}
            className={`event-speaker-card__caret ${
              isOpen ? 'event-speaker-card__caret--open' : ''
            }`}
          />
          <span>{title}</span>
        </button>
        <button
          type='button'
          className='event-speaker-card__delete'
          onClick={onDelete}
          aria-label='Delete speaker'
        >
          <Trash size={16} />
        </button>
      </div>
      {isOpen ? (
        <div className='event-speaker-card__body'>
          <div className='event-speaker-upload-row'>
            <div className='event-speaker-avatar'>
              {speaker?.image ? (
                <img src={speaker.image} alt={speaker?.name || 'Speaker avatar'} />
              ) : (
                <UserCircle size={52} />
              )}
            </div>
            <div className='event-speaker-upload-copy'>
              <p className='event-speaker-upload-copy__title'>
                Upload Image <span className='event-required-star'>*</span>
              </p>
              <p className='event-speaker-upload-copy__hint'>
                Min 400x400px, PNG or JPEG
              </p>
            </div>
            <button
              type='button'
              className='event-speaker-upload-btn'
              disabled
              aria-disabled='true'
            >
              <UploadSimple size={16} />
              <span>Upload</span>
            </button>
          </div>
          {(fieldTouched.image || shouldShowSubmitErrors) && errors?.image ? (
            <p className='error-text mb-0'>{errors.image}</p>
          ) : null}

          <div className='event-speaker-form-grid'>
            <div className='event-speaker-field'>
              <label htmlFor={`${title}-name`}>
                Name <span className='event-required-star'>*</span>
              </label>
              <SelectComponent
                key={`${title}-${speakerOptions.map((option) => option.value).join('|')}`}
                inputId={`${title}-name`}
                classNamePrefix='event-speaker-select'
                options={speakerOptions}
                selectedValue={selectedSpeakerValue}
                placeholder={
                  speakersLoading ? 'Loading speakers...' : 'Select Speaker'
                }
                isLoading={speakersLoading}
                isDisabled={speakersLoading}
                styles={speakerSelectStyles}
                menuPortalTarget={
                  typeof document !== 'undefined' ? document.body : null
                }
                menuPosition='fixed'
                menuPlacement='auto'
                handleChange={(option) => {
                  markTouched('name')
                  onSelectSpeaker(option)
                }}
                onBlur={() => markTouched('name')}
              />
              {(fieldTouched.name || shouldShowSubmitErrors) && errors?.name ? (
                <p className='error-text mb-0'>{errors.name}</p>
              ) : null}
            </div>
            <div className='event-speaker-field'>
              <label htmlFor={`${title}-mobile`}>Mobile Number</label>
              <div className='event-speaker-phone event-speaker-phone--readonly'>
                <div className='event-speaker-phone__prefix'>+966</div>
                <input
                  id={`${title}-mobile`}
                  value={speaker?.mobileNumber || ''}
                  placeholder='(555) 000-0000'
                  readOnly
                  tabIndex={-1}
                />
                <Info size={14} />
              </div>
            </div>
            <div className='event-speaker-field'>
              <label htmlFor={`${title}-designation`}>
                Designation <span className='event-required-star'>*</span>
              </label>
              <input
                id={`${title}-designation`}
                value={speaker?.designation || ''}
                placeholder='Type Here'
                readOnly
                tabIndex={-1}
                className='event-speaker-field__readonly'
              />
              {(fieldTouched.designation || shouldShowSubmitErrors) &&
              errors?.designation ? (
                <p className='error-text mb-0'>{errors.designation}</p>
              ) : null}
            </div>
            <div className='event-speaker-field'>
              <label htmlFor={`${title}-description`}>Description</label>
              <input
                id={`${title}-description`}
                value={speaker?.description || ''}
                placeholder='Type Here'
                readOnly
                tabIndex={-1}
                className='event-speaker-field__readonly'
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

SpeakerAccordionCard.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSelectSpeaker: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    image: PropTypes.string,
    name: PropTypes.string,
    designation: PropTypes.string,
  }),
  submitCount: PropTypes.number,
  speakersLoading: PropTypes.bool,
  speakerOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
      speaker: PropTypes.shape({
        name: PropTypes.string,
        userId: PropTypes.string,
        designation: PropTypes.string,
        description: PropTypes.string,
        image: PropTypes.string,
        mobileNumber: PropTypes.string,
        email: PropTypes.string,
      }),
    })
  ),
  speaker: PropTypes.shape({
    image: PropTypes.string,
    name: PropTypes.string,
    userId: PropTypes.string,
    mobileNumber: PropTypes.string,
    designation: PropTypes.string,
    description: PropTypes.string,
  }),
}

SpeakerAccordionCard.defaultProps = {
  speaker: {},
  speakerOptions: [],
  speakersLoading: false,
  errors: undefined,
  submitCount: 0,
}

export default SpeakerAccordionCard
